import styles from './QuickSearch.less';

import React, { Component, PropTypes } from 'react';
import { connect } from 'react-redux';
import chunk from 'chunk';
import { quickSearchViewModel } from '@seek/seek-refine-job-search';
import { ChevronIcon } from 'seek-style-guide/react';

import QualifiedLink from 'QualifiedLink/QualifiedLink';
import ExternalLink from 'ExternalLink/ExternalLink';

import {
  quickSearchClassificationClicked,
  quickSearchCityClicked,
  quickSearchGraduateClicked
} from 'store/search/search';

const AUcities = [
  {
    name: 'Sydney',
    query: { where: 'All Sydney NSW' }
  },
  {
    name: 'Melbourne',
    query: { where: 'All Melbourne VIC' }
  },
  {
    name: 'Brisbane',
    query: { where: 'All Brisbane QLD' }
  },
  {
    name: 'Gold Coast',
    query: { where: 'All Gold Coast QLD' }
  },
  {
    name: 'Perth',
    query: { where: 'All Perth WA' }
  },
  {
    name: 'Adelaide',
    query: { where: 'All Adelaide SA' }
  },
  {
    name: 'Hobart',
    query: { where: 'All Hobart TAS' }
  },
  {
    name: 'Darwin',
    query: { where: 'All Darwin NT' }
  },
  {
    name: 'Canberra',
    query: { where: 'All Canberra ACT' }
  }
];

const NZcities = [
  {
    name: 'Auckland',
    query: { where: 'All Auckland' }
  },
  {
    name: 'Hamilton',
    query: { where: 'Hamilton Waikato' }
  },
  {
    name: 'Tauranga',
    query: { where: 'Tauranga Bay of Plenty' }
  },
  {
    name: 'Wellington',
    query: { where: 'All Wellington' }
  },
  {
    name: 'Christchurch',
    query: { where: 'Christchurch Canterbury' }
  },
  {
    name: 'Dunedin',
    query: { where: 'Dunedin Otago' }
  }
];

const {
  classifications: {
    priority: priorityClassifications,
    all: allClassifications
  }
} = quickSearchViewModel();

const columnLength = Math.ceil(allClassifications.length / 3);
const classificationColumns = chunk(allClassifications, columnLength);

function mapStateToProps({ location }) {
  return {
    locale: location.locale
  };
}

const mapDispatchToProps = {
  onClassificationClick: quickSearchClassificationClicked,
  onCityClick: quickSearchCityClicked,
  onGraduateClick: quickSearchGraduateClicked
};

@connect(mapStateToProps, mapDispatchToProps)
export default class QuickSearch extends Component {

  static propTypes = {
    locale: PropTypes.string.isRequired,
    onClassificationClick: PropTypes.func.isRequired,
    onCityClick: PropTypes.func.isRequired,
    onGraduateClick: PropTypes.func.isRequired
  };

  shouldComponentUpdate(nextProps) {
    return this.props.locale !== nextProps.locale;
  }

  render() {
    const { locale, onClassificationClick, onCityClick, onGraduateClick } = this.props;
    const isAU = (locale === 'AU');
    const isNZ = (locale === 'NZ');
    const cities = (isAU ? AUcities : NZcities);

    return (
      <section className={styles.root} data-automation="quick-search-container">
        <div className={styles.content}>
          <h1 className={styles.heading}>Quick search </h1>

          <div className={styles.rowsContainer}>
            <div className={styles.row}>
              <h2 className={styles.rowHeading}>Classifications</h2>
              {
                priorityClassifications.map(({ description, searchQuery }) => (
                  <QualifiedLink
                    className={styles.link}
                    path="/jobs"
                    params={searchQuery}
                    aria-label={`Jobs in ${description}`}
                    analytics={`search-by-class-${description}`}
                    onClick={onClassificationClick}
                    scrollToTop={true}
                    key={description}
                    data-automation="quick-search-priority-classification-link">
                    {description}
                  </QualifiedLink>
                ))
              }

              <input id="allClassificationsToggle" type="checkbox" className={styles.toggle} />
              <label htmlFor="allClassificationsToggle" className={`${styles.link} ${styles.toggleLabel}`}>
                View all
                <ChevronIcon direction="down" className={styles.chevron} svgClassName={styles.chevronSvg} />
              </label>
              <span className={styles.toggleContent}>
                {
                  classificationColumns.map((column, i) => (
                    <span key={i} className={styles.column}>
                      {
                        column.map(({ description, searchQuery }) => (
                          <QualifiedLink
                            className={styles.link}
                            path="/jobs"
                            params={searchQuery}
                            aria-label={`Jobs in ${description}`}
                            analytics={`search-by-class-${description}`}
                            onClick={onClassificationClick}
                            scrollToTop={true}
                            key={description}
                            data-automation="quick-search-classification-link">
                            {description}
                          </QualifiedLink>
                        ))
                      }
                    </span>
                  ))
                }
              </span>
            </div>

            <div className={styles.row}>
              <h2 className={styles.rowHeading}>Major cities</h2>
              {
                cities.map(({ name, query }) => (
                  <QualifiedLink
                    className={styles.link}
                    path="/jobs"
                    params={query}
                    aria-label={`Jobs in ${name}`}
                    analytics={`search-by-city-${name}`}
                    onClick={onCityClick}
                    scrollToTop={true}
                    key={`Jobs in ${name}`}
                    data-automation="quick-search-city-link">
                    {name}
                  </QualifiedLink>
                ))
              }
            </div>

            <div className={styles.row}>
              <h2 className={styles.rowHeading}>Other</h2>
              {
                isAU && [
                  <QualifiedLink
                    key={1}
                    className={styles.link}
                    path="/jobs"
                    params={{ graduatesearch: 'true' }}
                    aria-label="Graduate / Entry Level jobs"
                    analytics="search-by-graduate"
                    onClick={onGraduateClick}
                    scrollToTop={true}
                    data-automation="quick-search-graduate-link">
                    Graduate / Entry Level
                  </QualifiedLink>,
                  <ExternalLink
                    key={2}
                    className={styles.link}
                    href="https://www.volunteer.com.au/volunteering"
                    data-automation="quick-search-volunteer-link">
                    Volunteer
                  </ExternalLink>,
                  <ExternalLink
                    key={3}
                    className={styles.link}
                    href="/Recruiter"
                    aria-label="Search by recruiter"
                    analytics="recruiter"
                    data-automation="quick-search-recruiters-link">
                    Search by Recruiter
                  </ExternalLink>
                ] ||
                isNZ && [
                  <ExternalLink
                    key={1}
                    className={styles.link}
                    href="/100kjobs"
                    aria-label="Jobs paying more than $100k"
                    analytics="100kjobs"
                    data-automation="quick-search-100kjobs-link">
                    Jobs paying more than $100k
                  </ExternalLink>,
                  <ExternalLink
                    key={2}
                    className={styles.link}
                    href="/Employer"
                    aria-label="Search By Employers"
                    analytics="search-by-employers"
                    data-automation="quick-search-employer-link">
                    Search by Employers
                  </ExternalLink>

                ]
              }
            </div>

          </div>
        </div>
      </section>
    );
  }

}



/** WEBPACK FOOTER **
 ** ./app/components/HomePage/components/QuickSearch/QuickSearch.js
 **/